import { AuthApi } from '../api/auth-api';
import { evaluatePasswordRules } from '../utils/password-validator';
import { ToastComponent } from '../components/toast';
import { UserProfile } from '../types';
import { escapeHtml } from '../utils/formatters';

export class AuthView {
  private onLoginSuccessCallback: (user: UserProfile) => void;

  constructor(onLoginSuccess: (user: UserProfile) => void) {
    this.onLoginSuccessCallback = onLoginSuccess;
    this.initEventListeners();
  }

  public initEventListeners(): void {
    // Tab switching
    const tabRegisterBtn = document.getElementById('tabRegisterBtn');
    const tabLoginBtn = document.getElementById('tabLoginBtn');

    tabRegisterBtn?.addEventListener('click', () => this.switchTab('register'));
    tabLoginBtn?.addEventListener('click', () => this.switchTab('login'));

    // Real-time password rules validation (Crucial specification)
    const pwdInput = document.getElementById('regPassword') as HTMLInputElement | null;
    pwdInput?.addEventListener('input', () => {
      this.updatePasswordCriteriaUI(pwdInput.value);
    });

    // Registration Form submit
    const regForm = document.getElementById('registerForm') as HTMLFormElement | null;
    regForm?.addEventListener('submit', (e) => this.handleRegister(e));

    // Login Form submit
    const loginForm = document.getElementById('loginForm') as HTMLFormElement | null;
    loginForm?.addEventListener('submit', (e) => this.handleLogin(e));
  }

  public switchTab(tab: 'register' | 'login'): void {
    const regBtn = document.getElementById('tabRegisterBtn');
    const loginBtn = document.getElementById('tabLoginBtn');
    const regPanel = document.getElementById('registerPanel');
    const loginPanel = document.getElementById('loginPanel');

    if (tab === 'register') {
      regBtn?.classList.add('active');
      loginBtn?.classList.remove('active');
      if (regPanel) regPanel.style.display = 'block';
      if (loginPanel) loginPanel.style.display = 'none';
    } else {
      loginBtn?.classList.add('active');
      regBtn?.classList.remove('active');
      if (loginPanel) loginPanel.style.display = 'block';
      if (regPanel) regPanel.style.display = 'none';
    }
  }

  private updatePasswordCriteriaUI(password: string): void {
    const rules = evaluatePasswordRules(password);

    this.toggleRuleItem('ruleLength', rules.minLength);
    this.toggleRuleItem('ruleUpper', rules.hasUpper);
    this.toggleRuleItem('ruleDigit', rules.hasDigit);
    this.toggleRuleItem('ruleSpecial', rules.hasSpecial);

    const strengthBar = document.getElementById('strengthBar');
    const strengthTag = document.getElementById('strengthTag');

    if (!strengthBar || !strengthTag) return;

    if (rules.score === 0) {
      strengthBar.style.width = '0%';
      strengthBar.style.background = 'transparent';
      strengthTag.textContent = 'En attente';
      strengthTag.style.color = 'var(--text-dim)';
    } else if (rules.score <= 2) {
      strengthBar.style.width = '35%';
      strengthBar.style.background = '#ef4444';
      strengthTag.textContent = 'Insuffisant';
      strengthTag.style.color = '#f87171';
    } else if (rules.score === 3) {
      strengthBar.style.width = '70%';
      strengthBar.style.background = '#f59e0b';
      strengthTag.textContent = 'Moyen';
      strengthTag.style.color = '#fbbf24';
    } else {
      strengthBar.style.width = '100%';
      strengthBar.style.background = '#10b981';
      strengthTag.textContent = 'Sécurisé ✓';
      strengthTag.style.color = '#34d399';
    }
  }

  private toggleRuleItem(elementId: string, isValid: boolean): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const icon = el.querySelector('.rule-icon');

    if (isValid) {
      el.classList.remove('invalid');
      el.classList.add('valid');
      if (icon) icon.textContent = '✓';
    } else {
      el.classList.remove('valid');
      el.classList.add('invalid');
      if (icon) icon.textContent = '✕';
    }
  }

  private async handleRegister(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const errorAlert = document.getElementById('registerErrorAlert');
    const successAlert = document.getElementById('registerSuccessAlert');
    if (errorAlert) errorAlert.style.display = 'none';
    if (successAlert) successAlert.style.display = 'none';

    try {
      const siret = (form.elements.namedItem('siret') as HTMLInputElement).value;
      const raison_sociale = (form.elements.namedItem('raison_sociale') as HTMLInputElement).value;
      const email = (form.elements.namedItem('email') as HTMLInputElement).value;
      const telephone = (form.elements.namedItem('telephone') as HTMLInputElement).value;
      const password = (form.elements.namedItem('password') as HTMLInputElement).value;

      const res = await AuthApi.register({
        siret,
        raison_sociale,
        email,
        telephone,
        password
      });

      if (res.requiresValidation || !res.token) {
        // Le compte doit être validé par un administrateur
        form.reset();
        this.updatePasswordCriteriaUI('');
        const siretCounter = document.getElementById('siretCounter');
        if (siretCounter) siretCounter.textContent = '0 / 14';

        if (successAlert) {
          successAlert.innerHTML = `
            <div style="display: flex; align-items: flex-start; gap: 0.75rem;">
              <span style="font-size: 1.5rem; line-height: 1;">⏳</span>
              <div>
                <strong style="display: block; font-size: 0.95rem; margin-bottom: 0.25rem;">Demande d'inscription enregistrée !</strong>
                <p style="margin: 0; font-size: 0.85rem; line-height: 1.4;">
                  Votre compte pour <strong>${escapeHtml(res.user.raison_sociale)}</strong> est actuellement <strong>en attente de validation par l'administrateur</strong>. 
                  Vous pourrez vous connecter dès que votre compte aura été approuvé.
                </p>
              </div>
            </div>
          `;
          successAlert.style.display = 'block';
        }

        // Pré-remplir l'identifiant dans le panneau de connexion
        const loginIdentifier = document.getElementById('loginIdentifier') as HTMLInputElement | null;
        if (loginIdentifier) {
          loginIdentifier.value = email;
        }

        ToastComponent.show('Inscription enregistrée. Votre compte est en cours de validation par un administrateur.', 'info', 7000);
      } else {
        ToastComponent.show(res.message, 'success');
        this.onLoginSuccessCallback(res.user);
      }
    } catch (err: any) {
      if (errorAlert) {
        errorAlert.textContent = err.message;
        errorAlert.style.display = 'block';
      }
      ToastComponent.show(err.message, 'error');
    }
  }

  private async handleLogin(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const errorAlert = document.getElementById('loginErrorAlert');
    if (errorAlert) errorAlert.style.display = 'none';

    try {
      const identifier = (form.elements.namedItem('identifier') as HTMLInputElement).value;
      const password = (form.elements.namedItem('password') as HTMLInputElement).value;

      const res = await AuthApi.login({ identifier, password });
      ToastComponent.show(`Bienvenue, ${res.user.raison_sociale} !`, 'success');
      this.onLoginSuccessCallback(res.user);
    } catch (err: any) {
      if (errorAlert) {
        errorAlert.textContent = err.message;
        errorAlert.style.display = 'block';
      }
      ToastComponent.show(err.message, 'error');
    }
  }
}
